import {createPortal} from "react-dom";
import {
  cloneElement,
  CSSProperties,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";

export type OtsTooltipPlacement = "top" | "bottom";
export type OtsTooltipDensity = "compact" | "default";

export type OtsTooltipProps = {
  content: ReactNode;
  children: ReactElement<{"aria-describedby"?: string}>;
  density?: OtsTooltipDensity;
  placement?: OtsTooltipPlacement;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  disabled?: boolean;
  className?: string;
  /** Extra classes for the floating panel, e.g. a wider `max-w-*` for a rich preview. */
  panelClassName?: string;
};

type TooltipPosition = {
  top: number;
  left: number;
  positioned: boolean;
};

const viewportPadding = 8;

/**
 * Where the floating panel renders: the trigger's own shadow root when it lives in one (WaterGo's
 * mount bridge), so the panel keeps that root's stylesheets; otherwise the document body.
 *
 * Headless UI's Portal is deliberately not used. Inside a Dialog it targets the dialog element and
 * then "re-attaches" any target that `document.body.contains` can't see, which for a dialog in a
 * shadow root moves the whole dialog out to the unstyled page.
 */
export const tooltipPortalTarget = (anchor: Element | null): Element | DocumentFragment => {
  const root = anchor?.getRootNode();
  if (typeof ShadowRoot !== "undefined" && root instanceof ShadowRoot) return root;
  return anchor?.ownerDocument.body ?? document.body;
};

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.min(Math.max(value, minimum), Math.max(minimum, maximum));

export type OtsTooltipPanelProps = {
  id: string;
  /** The element the panel points at; it may be markup React doesn't own, such as a GWT grid cell. */
  anchor: Element | null;
  content: ReactNode;
  density?: OtsTooltipDensity;
  placement?: OtsTooltipPlacement;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  panelClassName?: string;
  /** Defaults to the anchor's shadow root or document body (see `tooltipPortalTarget`). */
  portalTarget?: Element | DocumentFragment | null;
};

/** The floating tooltip panel, positioned against any anchor element and kept in the viewport. */
export const OtsTooltipPanel = ({
  id,
  anchor,
  content,
  density = "default",
  placement = "top",
  brand,
  colorScheme,
  panelClassName,
  portalTarget,
}: OtsTooltipPanelProps) => {
  const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<TooltipPosition>({top: 0, left: 0, positioned: false});

  const updatePosition = useCallback(() => {
    if (!anchor || !panelElement) return;

    const anchorRect = anchor.getBoundingClientRect();
    const panelRect = panelElement.getBoundingClientRect();
    const gap = density === "compact" ? 6 : 8;
    const desiredLeft = anchorRect.left + (anchorRect.width - panelRect.width) / 2;
    const left = clamp(desiredLeft, viewportPadding, window.innerWidth - panelRect.width - viewportPadding);
    const preferredTop = placement === "top"
      ? anchorRect.top - panelRect.height - gap
      : anchorRect.bottom + gap;
    const alternateTop = placement === "top"
      ? anchorRect.bottom + gap
      : anchorRect.top - panelRect.height - gap;
    const top = preferredTop < viewportPadding || preferredTop + panelRect.height > window.innerHeight - viewportPadding
      ? alternateTop
      : preferredTop;

    setPosition({
      top: clamp(top, viewportPadding, window.innerHeight - panelRect.height - viewportPadding),
      left,
      positioned: true,
    });
  }, [anchor, density, panelElement, placement]);

  useLayoutEffect(() => {
    if (!panelElement) return;
    updatePosition();
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      updatePosition();
      secondFrame = window.requestAnimationFrame(updatePosition);
    });
    const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(updatePosition);
    resizeObserver?.observe(panelElement);
    if (anchor) resizeObserver?.observe(anchor);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchor, panelElement, updatePosition]);

  const panelStyle: CSSProperties = {
    top: position.top,
    left: position.left,
    visibility: position.positioned ? "visible" : "hidden",
  };

  return createPortal(
    <OtsThemeRoot brand={brand} colorScheme={colorScheme}>
      <div
        ref={setPanelElement}
        id={id}
        role="tooltip"
        data-density={density}
        style={panelStyle}
        className={[
          "pointer-events-none fixed z-[1300] max-w-72 border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] text-[var(--ots-text)] shadow-lg",
          density === "compact" ? "rounded px-2 py-1 text-[10px] leading-4" : "rounded-md px-2.5 py-1.5 text-xs leading-4",
          panelClassName,
        ].filter(Boolean).join(" ")}>
        {content}
      </div>
    </OtsThemeRoot>,
    portalTarget ?? tooltipPortalTarget(anchor),
  );
};

/** Theme-aware pointer and keyboard tooltip with compact density and portal-safe tokens. */
export const OtsTooltip = ({
  content,
  children,
  density = "default",
  placement = "top",
  brand,
  colorScheme,
  disabled = false,
  className,
  panelClassName,
}: OtsTooltipProps) => {
  const id = useId();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    const dismiss = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", dismiss);
    return () => document.removeEventListener("keydown", dismiss);
  }, [open]);

  if (content == null) return children;

  const visible = open && !disabled;
  const describedBy = [children.props["aria-describedby"], visible ? id : undefined].filter(Boolean).join(" ") || undefined;

  return <span
    ref={anchorRef}
    className={["inline-flex", className].filter(Boolean).join(" ")}
    onMouseEnter={() => !disabled && setOpen(true)}
    onMouseLeave={() => setOpen(false)}
    onFocusCapture={() => !disabled && setOpen(true)}
    onBlurCapture={event => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false);
    }}>
    {cloneElement(children, {"aria-describedby": describedBy})}
    {visible && <OtsTooltipPanel
      id={id}
      anchor={anchorRef.current}
      content={content}
      density={density}
      placement={placement}
      brand={brand}
      colorScheme={colorScheme}
      panelClassName={panelClassName}
    />}
  </span>;
};
